/**
 * AdPlaceholder Component
 * 
 * Displays a blue placeholder box in development mode to visualize ad placements.
 * In production mode, renders actual Google AdSense ad units.
 * Respects the ads_enabled and app_environment settings from site settings.
 * 
 * Usage:
 *   <AdPlaceholder slot="sidebar" />
 *   <AdPlaceholder slot="in-feed" />
 *   <AdPlaceholder slot="banner" />
 */
import { useEffect, useRef } from 'react';
import { useSiteSettings } from '@/hooks/useApiQueries';

const AD_SIZES = {
  // Sidebar ad - vertical rectangle
  sidebar: {
    width: '300px',
    height: '250px',
    label: 'Sidebar Ad (300x250)'
  },
  // In-feed ad - horizontal
  'in-feed': {
    width: '100%',
    height: '120px',
    label: 'In-Feed Ad'
  },
  // Banner ad - full width
  banner: {
    width: '100%',
    height: '90px',
    label: 'Banner Ad (728x90)'
  },
  // Leaderboard - top of page
  leaderboard: {
    width: '100%',
    height: '90px',
    label: 'Leaderboard Ad'
  },
  // Large rectangle
  'large-rectangle': {
    width: '336px',
    height: '280px',
    label: 'Large Rectangle (336x280)'
  },
  // Mobile banner
  'mobile-banner': {
    width: '100%',
    height: '50px',
    label: 'Mobile Banner (320x50)'
  }
};

const AdPlaceholder = ({ slot = 'sidebar', className = '', dataAdSlot = '' }) => {
  const { data: siteSettings, isLoading } = useSiteSettings();
  const adRef = useRef(null);
  const size = AD_SIZES[slot] || AD_SIZES.sidebar;
  
  // Use global app_environment setting to determine dev/prod mode
  const isDevMode = siteSettings?.app_environment !== 'prod';
  
  // Check if ads are enabled
  // In dev mode: show placeholder while loading, then respect setting
  // In prod mode: don't show anything until we know ads are enabled
  const adsEnabled = isDevMode 
    ? (isLoading || siteSettings?.ads_enabled === true)
    : (!isLoading && siteSettings?.ads_enabled === true);
  
  // Push ad when component mounts in production
  useEffect(() => {
    if (!isDevMode && adsEnabled && adRef.current) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        console.error('AdSense error:', e);
      }
    }
  }, [isDevMode, adsEnabled]);
  
  // Don't render anything if ads are disabled (and we know for sure)
  if (!isLoading && siteSettings?.ads_enabled !== true) {
    return null;
  }
  
  // In dev mode: don't render while loading settings (to avoid flash)
  if (isDevMode && isLoading) {
    return null;
  }
  
  // In dev mode, show blue placeholder
  if (isDevMode) {
    return (
      <div 
        className={`flex items-center justify-center bg-blue-500/20 border-2 border-dashed border-blue-500 rounded-lg ${className}`}
        style={{ 
          width: size.width, 
          height: size.height,
          minHeight: size.height
        }}
        data-testid={`ad-placeholder-${slot}`}
      >
        <div className="text-center p-4">
          <p className="text-blue-600 font-mono text-xs uppercase tracking-wider font-semibold">
            {size.label}
          </p>
          <p className="text-blue-500/70 text-[10px] mt-1">
            Google AdSense (Dev Mode)
          </p>
        </div>
      </div>
    );
  }
  
  // In production mode, render actual AdSense unit
  // Note: We don't set a fixed height - AdSense will size the ad dynamically.
  // If no ad is served, the container collapses to 0 height.
  return (
    <ins 
      ref={adRef}
      className={`adsbygoogle ${className}`}
      style={{ display: 'block' }}
      data-ad-client="ca-pub-5780682619799625"
      data-ad-slot={dataAdSlot}
      data-ad-format="auto"
      data-full-width-responsive="true"
    />
  );
};

export default AdPlaceholder;
